import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["order"],
    _excluded2 = ["children", "theme"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { borders, color, display, maxWidth, minWidth, position, top, bottom, left, right, space, style, textAlign, verticalAlign, width, boxShadow, zIndex } from 'styled-system';
import UpIcon from '../Icons/UpIcon';
import DownIcon from '../Icons/DownIcon';
import { textStyle, cursor } from '../theme';
import { boxSpacer } from '../space';
import { remapHoverProps, hoverFocus } from '../focus';
import { useThemeDefaults, innerPaddingX, pipe, whiteSpace } from '../utils';
import Box from '../Box';
var tableLayout = style({
  prop: 'tableLayout'
});
tableLayout.propTypes = {
  tableLayout: PropTypes.string
};

var textTransform = function textTransform(props) {
  return props.textTransform ? {
    textTransform: props.textTransform
  } : [];
};

textTransform.propTypes = {
  textTransform: PropTypes.oneOf(['none', 'capitalize', 'uppercase', 'lowercase', 'initial', 'inherit'])
};
var hoverColor = pipe(remapHoverProps, color, hoverFocus);
hoverColor.propTypes = remapHoverProps.propTypes;
var Table = styled('table').withConfig({
  displayName: "Table",
  componentId: "sc-15luk7c-0"
})({
  borderCollapse: 'collapse'
}, minWidth, maxWidth, width, boxSpacer, space, display, tableLayout, position, top, bottom, left, right);
Table.defaultProps = {
  width: '100%'
};
Table.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, minWidth.propTypes), maxWidth.propTypes), width.propTypes), boxSpacer.propTypes), space.propTypes), display.propTypes), tableLayout.propTypes);
export var TBody = Table.TBody = styled('tbody').withConfig({
  displayName: "Table__TBody",
  componentId: "sc-15luk7c-1"
})([""]);
export var THead = Table.THead = useThemeDefaults(function (props) {
  return {
    borderBottom: "".concat(props.theme.borderWidth, " solid"),
    borderTop: "".concat(props.theme.borderWidth, " solid"),
    borderColor: "keyline"
  };
}, styled("thead").withConfig({
  displayName: "Table__THead",
  componentId: "sc-15luk7c-2"
})(borders, display));
THead.propTypes = _objectSpread({}, borders.propTypes); // don't space outermost pads, see `innerPaddingX`

var innerPaddingSpace = pipe(space, innerPaddingX);
export var THBase = Table.THBase = useThemeDefaults(function (_ref) {
  var theme = _ref.theme;
  return {
    textStyle: 'bodySmallMedium',
    textAlign: 'left',
    textTransform: 'uppercase',
    color: theme.th.color,
    bg: theme.th.bg,
    paddingTop: theme.th.paddingY,
    paddingBottom: theme.th.paddingY,
    paddingLeft: theme.th.paddingX,
    paddingRight: theme.th.paddingX // position: 'relative'

  };
}, styled('th').withConfig({
  displayName: "Table__THBase",
  componentId: "sc-15luk7c-3"
})(display, textStyle, textAlign, textTransform, color, borders, boxShadow, minWidth, maxWidth, width, cursor, position, top, bottom, left, right, innerPaddingSpace, verticalAlign, whiteSpace, zIndex));

var orderProps = function orderProps(_ref2) {
  var theme = _ref2.theme;
  return {
    display: 'inline',
    width: '0.75em',
    height: '0.75em',
    color: theme.th.color,
    pl: '3px'
  };
};

export var TH = Table.TH = withTheme(function (_ref3) {
  var order = _ref3.order,
      props = _objectWithoutProperties(_ref3, _excluded);

  return /*#__PURE__*/React.createElement(THBase, props, props.children, order ? "\xA0" : "", order === "asc" ? /*#__PURE__*/React.createElement(UpIcon, orderProps(props)) : order === "desc" ? /*#__PURE__*/React.createElement(DownIcon, orderProps(props)) : null);
});
TH.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, textStyle.propTypes), textAlign.propTypes), textTransform.propTypes), color.propTypes), space.propTypes), width.propTypes), verticalAlign.propTypes), display.propTypes), cursor.propTypes), whiteSpace.propTypes);
export var TR = Table.TR = useThemeDefaults(function (props) {
  return {
    hoverBg: !props.hoverStyles || props.selected ? undefined : props.theme.colors.bgPlaceholder,
    bg: props.selected ? props.theme.colors.ctrlBg : props.theme.colors.bg,
    borderBottom: "".concat(props.theme.borderWidth, " solid"),
    borderColor: "keyline"
  };
}, styled("tr").withConfig({
  displayName: "Table__TR",
  componentId: "sc-15luk7c-4"
})(display, color, cursor, hoverColor, borders));
TR.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, display.propTypes), color.propTypes), hoverColor.propTypes), borders.propTypes);
export var TD = Table.TD = useThemeDefaults(function (props) {
  return {
    textStyle: 'bodyRegular',
    textAlign: 'left',
    color: props.theme.td.color,
    paddingTop: props.theme.td.paddingY,
    paddingBottom: props.theme.td.paddingY,
    paddingLeft: props.theme.td.paddingX,
    paddingRight: props.theme.td.paddingX,
    verticalAlign: 'middle'
  };
}, styled('td').withConfig({
  displayName: "Table__TD",
  componentId: "sc-15luk7c-5"
})(display, textStyle, textAlign, textTransform, color, cursor, borders, boxShadow, innerPaddingSpace, position, top, bottom, left, right, width, minWidth, maxWidth, verticalAlign, whiteSpace));
TD.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, display.propTypes), textStyle.propTypes), textAlign.propTypes), textTransform.propTypes), color.propTypes), space.propTypes), width.propTypes), verticalAlign.propTypes), whiteSpace.propTypes);
export var Placeholder = Table.Placeholder = withTheme(function (_ref4) {
  var children = _ref4.children,
      theme = _ref4.theme,
      props = _objectWithoutProperties(_ref4, _excluded2);

  return /*#__PURE__*/React.createElement(Box, _extends({
    minHeight: theme.tablePlaceholder.minHeight,
    bg: theme.colors.bgPlaceholder,
    borderRadius: theme.radius,
    padding: theme.tablePlaceholder.padding,
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center"
  }, props), /*#__PURE__*/React.createElement(Box, {
    textAlign: "center",
    maxWidth: theme.tablePlaceholder.maxWidth
  }, children));
});
Placeholder.propTypes = Box.propTypes;
export default Table;